import {Controller} from "@hotwired/stimulus"

// Replie une section épinglée (qui ne défile pas avec la liste) dès que
// l'utilisateur quitte le haut de la liste, et la rouvre quand il y revient.
// Utilisé par la rangée « À présenter cette semaine » du fil, dont les avatars
// mangent l'écran sur mobile une fois qu'on défile.
export default class extends Controller {
    static targets = ["collapsible"]

    static values = {
        scroller: String,
        // Hystérésis : on replie après un vrai défilement, on rouvre seulement
        // une fois de retour tout en haut — sinon la section clignote.
        collapseAt: {type: Number, default: 64},
        expandAt: {type: Number, default: 8}
    }

    connect() {
        this.collapsed = false
        this.onScroll = this.onScroll.bind(this)
        this.scrollers = this.resolveScrollers()

        this.scrollers.forEach((scroller) => {
            scroller.addEventListener("scroll", this.onScroll, {passive: true})
        })

        this.update()
    }

    disconnect() {
        this.scrollers?.forEach((scroller) => {
            scroller.removeEventListener("scroll", this.onScroll)
        })
        if (this.frame) cancelAnimationFrame(this.frame)
        this.frame = null
    }

    onScroll() {
        if (this.frame) return

        this.frame = requestAnimationFrame(() => {
            this.frame = null
            this.update()
        })
    }

    update() {
        const scrollTop = this.scrollTop()

        if (this.collapsed && scrollTop <= this.expandAtValue) {
            this.setCollapsed(false)
        } else if (!this.collapsed && scrollTop >= this.collapseAtValue) {
            this.setCollapsed(true)
        }
    }

    setCollapsed(collapsed) {
        this.collapsed = collapsed

        this.collapsibleTargets.forEach((target) => {
            target.classList.toggle("is-collapsed-js", collapsed)
            target.setAttribute("aria-hidden", collapsed ? "true" : "false")
        })
    }

    // La page peut défiler soit dans le conteneur désigné (la liste du fil),
    // soit dans un ancêtre scrollable du layout : on écoute les deux et on
    // retient le défilement le plus avancé.
    resolveScrollers() {
        const scrollers = []

        if (this.hasScrollerValue) {
            const designated = document.querySelector(this.scrollerValue)
            if (designated) scrollers.push(designated)
        }

        const ancestor = this.closestScrollable()
        if (ancestor && !scrollers.includes(ancestor)) scrollers.push(ancestor)

        return scrollers.length ? scrollers : [window]
    }

    closestScrollable() {
        let element = this.element.parentElement

        while (element && element !== document.body) {
            const overflowY = window.getComputedStyle(element).overflowY
            if (overflowY === "auto" || overflowY === "scroll") return element
            element = element.parentElement
        }

        return null
    }

    scrollTop() {
        return Math.max(
            ...this.scrollers.map((scroller) => (scroller === window ? window.scrollY : scroller.scrollTop))
        )
    }
}
